import React, { useEffect, useRef, useState } from 'react';

const LOGO_FALL_MS = 450;
const LOGO_GAP_MS = 90;
const DINGONET_LOGO = '/dingonetlogo-01.png';
const TAGLINE_WORDS = ['Together.', 'We', 'Respond.', 'Rebuild.', 'Restore'];
const TAGLINE_WORD_MS = 550;
const TAGLINE_FULL_MS = 700;

function useInView(options = {}) {
  const [isInView, setIsInView] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        if (options.once !== false) {
          observer.unobserve(entry.target);
        }
      } else if (options.once === false) {
        setIsInView(false);
      }
    }, options.config || { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });

    observer.observe(element);
    return () => observer.disconnect();
  }, [options.once, options.config]);

  return [elementRef, isInView];
}

export default function MemberLogoConvergence({ members: memberList, className = '' }) {
  const [sectionRef, inView] = useInView({ once: true, config: { threshold: 0.25 } });
  const [settledCount, setSettledCount] = useState(0);
  const [fallingIndex, setFallingIndex] = useState(-1);
  const [isFinale, setIsFinale] = useState(false);
  const [taglineStep, setTaglineStep] = useState(-1);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timersRef = useRef([]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    if (!inView) return undefined;

    if (reducedMotion) {
      setSettledCount(memberList.length);
      setIsFinale(true);
      setTaglineStep(TAGLINE_WORDS.length);
      return undefined;
    }

    setSettledCount(0);
    setFallingIndex(-1);
    setIsFinale(false);
    setTaglineStep(-1);

    let index = 0;

    const schedule = (fn, delay) => {
      const id = setTimeout(fn, delay);
      timersRef.current.push(id);
    };

    const runNext = () => {
      if (index >= memberList.length) {
        schedule(() => setIsFinale(true), LOGO_FALL_MS * 0.4);
        return;
      }

      setFallingIndex(index);
      schedule(() => {
        setSettledCount(index + 1);
        setFallingIndex(-1);
        index += 1;
        schedule(runNext, LOGO_GAP_MS);
      }, LOGO_FALL_MS);
    };

    schedule(runNext, 400);

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [inView, reducedMotion, memberList.length]);

  useEffect(() => {
    if (!isFinale) {
      setTaglineStep(-1);
      return undefined;
    }

    if (reducedMotion) {
      setTaglineStep(TAGLINE_WORDS.length);
      return undefined;
    }

    let step = 0;
    setTaglineStep(0);

    const schedule = (fn, delay) => {
      const id = setTimeout(fn, delay);
      timersRef.current.push(id);
    };

    const advanceTagline = () => {
      step += 1;
      if (step < TAGLINE_WORDS.length) {
        setTaglineStep(step);
        schedule(advanceTagline, TAGLINE_WORD_MS);
      } else {
        setTaglineStep(TAGLINE_WORDS.length);
      }
    };

    schedule(advanceTagline, TAGLINE_WORD_MS);

    return undefined;
  }, [isFinale, reducedMotion]);

  const showFullTagline = taglineStep >= TAGLINE_WORDS.length;

  return (
    <div
      ref={sectionRef}
      className={`relative mx-auto max-w-6xl overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0B1629] via-slate-900 to-[#0B1629] ${className}`.trim()}
    >
      <style>{`
        @keyframes member-logo-fall {
          0% {
            transform: translate(-50%, -140%) scale(1.05);
            opacity: 0;
          }
          12% {
            opacity: 1;
          }
          72% {
            opacity: 1;
          }
          100% {
            transform: translate(-50%, 88px) scale(0.28);
            opacity: 0;
          }
        }
        @keyframes hub-pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(0, 229, 204, 0.25);
          }
          50% {
            box-shadow: 0 0 32px 8px rgba(0, 229, 204, 0.2);
          }
        }
        @keyframes dingonet-reveal {
          0% {
            transform: scale(0.6);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        @keyframes tagline-word-in {
          0% {
            opacity: 0;
            transform: translateY(12px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes tagline-full-in {
          0% {
            opacity: 0;
            transform: scale(0.96);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .tagline-word {
          animation: tagline-word-in 400ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .tagline-full {
          animation: tagline-full-in ${TAGLINE_FULL_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .member-logo-falling {
          animation: member-logo-fall ${LOGO_FALL_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        .dingonet-finale {
          animation: dingonet-reveal 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .convergence-hub-pulse {
          animation: hub-pulse 2.4s ease-in-out infinite;
        }
      `}</style>

      <div className="relative flex min-h-[22rem] flex-col items-center justify-center px-6 py-10 md:min-h-[26rem]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,rgba(0,229,204,0.12),transparent_58%)]" />

        <div className="relative z-10 flex h-56 w-56 items-center justify-center md:h-64 md:w-64">
          {!isFinale ? (
            <div
              className={`absolute inset-6 rounded-full border-2 border-dashed border-[#00E5CC]/35 bg-[#00E5CC]/5 ${
                settledCount > 0 ? 'convergence-hub-pulse' : ''
              }`}
              aria-hidden="true"
            />
          ) : null}

          {memberList.map((member, i) => {
            if (i >= settledCount) return null;
            const angle = (i / memberList.length) * Math.PI * 2 - Math.PI / 2;
            const radius = isFinale ? 46 : 68;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            const size = isFinale ? 26 : 28;

            return (
              <div
                key={member.name}
                className={`absolute flex items-center justify-center rounded-full border border-white/15 bg-white p-1 shadow-md transition-all duration-500 ${
                  isFinale ? 'z-10' : 'z-20'
                }`}
                style={{
                  width: size,
                  height: size,
                  left: '50%',
                  top: '50%',
                  transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                  opacity: isFinale ? 0.92 : 0.75,
                  transitionDelay: `${i * 20}ms`,
                }}
              >
                <img
                  src={`/assets/${encodeURIComponent(member.logo)}`}
                  alt=""
                  className="h-full w-full object-contain"
                />
              </div>
            );
          })}

          {fallingIndex >= 0 && !isFinale && memberList[fallingIndex]?.logo ? (
            <div
              key={`falling-${fallingIndex}`}
              className="member-logo-falling absolute left-1/2 top-0 z-20 flex h-16 w-16 items-center justify-center rounded-full border border-[#00E5CC]/40 bg-white p-2 shadow-lg shadow-[#00E5CC]/20"
            >
              <img
                src={`/assets/${encodeURIComponent(memberList[fallingIndex].logo)}`}
                alt=""
                className="h-full w-full object-contain"
              />
            </div>
          ) : null}

          {isFinale ? (
            <div className="dingonet-finale relative z-30 flex h-36 w-36 items-center justify-center rounded-full border-2 border-[#00E5CC]/50 bg-white p-3 shadow-[0_0_48px_rgba(0,229,204,0.35)] md:h-40 md:w-40">
              <img src={DINGONET_LOGO} alt="DINGONET" className="h-full w-full object-contain" />
            </div>
          ) : null}
        </div>

        {isFinale ? (
          <h3 className="relative z-10 mt-2 flex min-h-[3rem] max-w-xl items-center justify-center text-center md:min-h-[3.5rem]">
            {showFullTagline ? (
              <span className="tagline-full text-lg font-bold leading-snug text-white md:text-2xl">
                <span className="text-[#00E5CC]">Together.</span> We{' '}
                <span className="text-[#00E5CC]">Respond.</span> <span className="text-[#00E5CC]">Rebuild.</span>{' '}
                <span className="text-[#00E5CC]">Restore</span>
              </span>
            ) : taglineStep >= 0 ? (
              <span
                key={taglineStep}
                className="tagline-word text-2xl font-bold text-[#00E5CC] md:text-4xl"
              >
                {TAGLINE_WORDS[taglineStep]}
              </span>
            ) : null}
          </h3>
        ) : (
          <>
            <p className="relative z-10 mt-2 text-center text-xs uppercase tracking-widest text-[#00E5CC]">
              Stronger Together
            </p>
            <h3 className="relative z-10 mt-2 text-center text-xl font-bold text-white md:text-2xl">
              20+ Organizations, One Purpose
            </h3>
          </>
        )}
        {!isFinale && !reducedMotion ? (
          <p className="relative z-10 mt-2 text-center text-xs text-slate-500">
            {settledCount} of {memberList.length} organizations joining the network
          </p>
        ) : null}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0B1629] to-transparent" />
    </div>
  );
}
